import {translate} from 'views/module';

import moment from 'moment-timezone';

const countFormatter = new Intl.NumberFormat('en-CA', {maximumFractionDigits: 0});
const hoursFormatter = new Intl.NumberFormat('en-CA', {maximumFractionDigits: 2});
const currencyFormatter = new Intl.NumberFormat('en-CA', {style: 'currency', currency: 'CAD'});

// Missing means null/undefined/NaN. Zero is a legitimate value and must never be
// treated as missing.
const isMissingValue = (value) => {
  return value === null || value === undefined || (typeof value === 'number' && Number.isNaN(value));
};

const unavailableText = () => {
  return translate('metrics.ClientMetrics.Unavailable');
};

const formatCount = (value) => {
  return isMissingValue(value) ? unavailableText() : countFormatter.format(value);
};

const formatHours = (value) => {
  return isMissingValue(value) ? unavailableText() : hoursFormatter.format(value);
};

const formatCurrency = (value) => {
  return isMissingValue(value) ? unavailableText() : currencyFormatter.format(value);
};

// Gross margin is stored as a ratio (0.4057 -> '40.6%').
const formatMargin = (value) => {
  return isMissingValue(value) ? unavailableText() : `${(value * 100).toFixed(1)}%`;
};

const formatByKind = (value, kind) => {
  if (kind === 'currency') {
    return formatCurrency(value);
  }

  if (kind === 'hours') {
    return formatHours(value);
  }

  if (kind === 'margin') {
    return formatMargin(value);
  }

  return formatCount(value);
};

// ISO dates (period start/end/through) arrive as 'YYYY-MM-DD'. They are calendar dates
// with no time component, so parse them as plain dates rather than shifting by timezone.
const formatIsoDate = (value) => {
  if (!value) {
    return unavailableText();
  }

  const dateMoment = moment(value, 'YYYY-MM-DD', true);

  return dateMoment.isValid() ? dateMoment.format('MMM D, YYYY') : unavailableText();
};

// 'Actual through July 21' style day label for the current period's throughDate.
const formatThroughDate = (value) => {
  if (!value) {
    return unavailableText();
  }

  const dateMoment = moment(value, 'YYYY-MM-DD', true);

  return dateMoment.isValid() ? dateMoment.format('MMMM D') : unavailableText();
};

// 'YYYY-MM' -> 'July 2026'.
const formatMonthLabel = (month) => {
  if (!month) {
    return unavailableText();
  }

  const monthMoment = moment(month, 'YYYY-MM', true);

  return monthMoment.isValid() ? monthMoment.format('MMMM YYYY') : unavailableText();
};

// Actual rates are historical and may legitimately differ across a month. The backend
// supplies the distinct rate(s) it observed; render them accurately rather than blending.
// A missing/empty list is unavailable, one or two rates are listed verbatim, and anything
// larger collapses to a translated 'Multiple' so the cell never sprawls.
const formatRateList = (rates) => {
  const values = (rates || []).filter((rate) => !isMissingValue(rate));

  if (!values.length) {
    return unavailableText();
  }

  if (values.length <= 2) {
    return values.map((rate) => currencyFormatter.format(rate)).join(', ');
  }

  return translate('metrics.ClientMetrics.ClientMonthly.Table.MultipleRates');
};

// Signed delta chip for actual-vs-scheduled variance. goodDirection states which way the
// metric should move ('up', 'down', or 'neutral') so the chip tone reflects business
// meaning instead of raw sign. Returns null when either side is missing (never treat a
// missing value as zero) — the caller then omits the chip.
const buildVariance = (scheduledValue, actualValue, {kind = 'count', goodDirection = 'neutral'} = {}) => {
  if (isMissingValue(scheduledValue) || isMissingValue(actualValue)) {
    return null;
  }

  const delta = actualValue - scheduledValue;
  const direction = delta > 0 ? 'up' : delta < 0 ? 'down' : 'flat';
  let tone = 'neutral';

  if (delta !== 0 && goodDirection !== 'neutral') {
    const isGood = goodDirection === 'up' ? delta > 0 : delta < 0;

    tone = isGood ? 'positive' : 'negative';
  }

  let label = '';

  if (kind === 'margin') {
    // Margin variance is expressed in percentage points, not a scaled ratio.
    const deltaPoints = delta * 100;
    const pointsPrefix = deltaPoints > 0 ? '+' : '';

    label = translate('metrics.ClientMetrics.MarginPoints', {
      value: `${pointsPrefix}${deltaPoints.toFixed(1)}`,
    });
  } else {
    // formatByKind already signs negatives; only positives need an explicit '+'.
    const signPrefix = delta > 0 ? '+' : '';

    label = `${signPrefix}${formatByKind(delta, kind)}`;
  }

  return {direction, tone, label};
};

export default {
  isMissingValue,
  unavailableText,
  formatCount,
  formatHours,
  formatCurrency,
  formatMargin,
  formatByKind,
  formatIsoDate,
  formatThroughDate,
  formatMonthLabel,
  formatRateList,
  buildVariance,
};
